import { useState, useEffect } from "react";
import { onAuthStateChanged, signInAnonymously } from "firebase/auth";

import CreatorChatHeader from "../components/GPT/chat-header";
import ChatEmptyScreen from "../components/chat/chat-empty-screen";
import { useChat } from "../hooks/use-chat";
import Messages from "../components/chat/messages";
import ChatInput from "../components/chat/chat-input";
import { auth } from "../utils/firebase";
import { AlertDialog } from "../components/ThemedComponents/AlertDialog";

export default function BuilderGpt({
  agents,
  darkMode,
}: {
  agents: string[];
  darkMode: boolean;
}) {
  // redeploy on done
  return <BuilderGptView agents={agents} darkMode={darkMode} />;
}

function BuilderGptView({
  agents,
  darkMode,
}: {
  agents: string[];
  darkMode: boolean;
}) {
  const [showHelp, setShowHelp] = useState<boolean>(false);
  const {
    messages,
    setMessages,
    loading,
    appendMessage,
    input,
    setInput,
    setAccessToken,
    error,
    setError,
    sendMessageHidden,
    sendUserSignature,
    accessToken,
    submit
  } = useChat();

  useEffect(() => {
    onAuthStateChanged(auth, async (user) => {
      if (user) {
        setAccessToken(await user.getIdToken(true));
      } else {
        setAccessToken(null);
        signInAnonymously(auth);
      }
    });
  }, []);

  const agentsInfo = [
    "- @Knowledge_Agent - general knowledge agent that can answer questions. It's also the default.",
    "- @DexV2, @DexV3 - agents specifically for Kinetix.Finance's Dex V2 and V3 respectively",
    "- @PerpV1, @PerpV2 - agents specifically for Kinetix.Finance's Perpetual Exchange V1 and V2 respectively",
  ];
  const promptExamples = [
    "- I want to swap 1 KAVA to USDt with @DexV2/@DexV3",
    "- I want to open a long position on Kava with 40 Kava and 2x leverage on @PerpV1",
    "- I want to close 100% of my opened long position on Kava on @PerpV1",
    "- I want to open a LONG/SHORT market with 26  KAVA  on KAVA-USD @PerpV2 leverage 1.1x",
    "- I want to close 100% of my KAVA long position for the ETH-USD market on @PerpV2",
    "- I want to create a LONG/SHORT limit order on @PerpV2 with 26 KAVA on USD-JPY market with 1.1x leverage price 130",
    "- I want to cancel a LONG/SHORT limit order on @PerpV2 with 26 KAVA on USD-JPY market with 1.1x leverage",
    "- I want to open a LONG limit order on @PerpV2, using 26 KAVA as deposit token, with a leverage of 1.5x, on ATOM-USD market, with a limit price of 2 and please set the take profit on 49",
    "- I want to open a SHORT position on @PerpV2, using 25.5 KAVA as deposit token, with a leverage of 1.1x, on ETH-USD market, with a stop loss on 4950",
  ];

  return (
    <div className="bg-gray-50 dark:bg-[#2c2c35] w-full relative">
      <AlertDialog
        open={!!error}
        title={"Oops!"}
        description={error}
        actionLabel="Accept"
        onActionPress={() => setError("")}
      />
      <AlertDialog
        open={showHelp}
        title={
          "How to interact with Kava blockchain protocols with Surface Area"
        }
        description={
          <div className="text-left space-y-2">
            <p>1. Connect wallet</p>
            <p>
              {`2. Determine which agent to talk to:        
                `}
              <ul className="px-4">
                {agentsInfo.map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            </p>

            <p>
              {`3. Type what you want to do:                
                `}
              <ul className="px-4">
                {promptExamples.map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            </p>
            <p>
              4. Review the given quote and confirm the transaction by signing
              with your connected wallet
            </p>
            <p>
              5. Wait for confirmation and check{" "}
              <a
                href="https://Kinetix.Finance"
                target="blank"
                className="text-blue-500"
              >
                https://Kinetix.Finance
              </a>{" "}
              dashboard for updates
            </p>
          </div>
        }
        actionLabel="Ok"
        onActionPress={() => setShowHelp(false)}
      />

      <div className="h-full flex flex-col">
        <CreatorChatHeader showHelp={() => setShowHelp(true)} />
        {messages.length === 0 ? (
          <ChatEmptyScreen setInput={setInput} />
        ) : (
          <Messages
            messages={messages}
            setMessages={setMessages}
            extraSendMessage={sendMessageHidden}
            sendUserSignature={sendUserSignature}
            sendMessageHidden={sendMessageHidden}
            loading={loading}
            accessToken={accessToken}
            setError={setError}
            submit={submit}
          />
        )}

        <ChatInput
          agents={agents}
          darkMode={darkMode}
          loading={loading}
          messages={messages}
          input={input}
          setInput={setInput}
          appendMessage={appendMessage}
          onClick={() => appendMessage()}
        />
      </div>
    </div>
  );
}
